import { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { ChartFilterBar, LineChartCard } from "../../../components/Charts";
import type { FilterDefinition, FilterValues } from "../../../types/chartFilters";
import type { FilterOnChange } from "../../../components/Charts/ChartFilterBar";
import { makeYearRangeFilter, P2_SISTEMA_OPTIONS } from "../common/sharedFilterDefs";
import { useP2Data, type P2FilterValues } from "./useP2ChartData";
import CompareButton from "../common/CompareButton";

const INITIAL_AMBITS_COUNT = 5;

const DEFAULT_FILTERS: P2FilterValues = {
  yearRange: undefined,
  sistema:   "Catalunya",
  ambit:     "",
  chart1Ambits: [],
};

const P2ChartSection = () => {
  const [filters, setFilters] = useState<P2FilterValues>(DEFAULT_FILTERS);
  const { chart1Data, chart1Lines, chart2Data, chart2Lines, availableAmbits, loading, error } =
    useP2Data(filters);

  useEffect(() => {
    if (availableAmbits.length > 0 && !filters.ambit) {
      setFilters((prev) => ({
        ...prev,
        ambit: availableAmbits[0],
        chart1Ambits: prev.chart1Ambits.length === 0
          ? availableAmbits.slice(0, INITIAL_AMBITS_COUNT)
          : prev.chart1Ambits,
      }));
    }
  }, [availableAmbits, filters.ambit]);

  const ambitOptions = useMemo(
    () => availableAmbits.map((a) => ({ value: a, label: a })),
    [availableAmbits]
  );

  const yearRangeFilters: FilterDefinition[] = useMemo(
    () => [makeYearRangeFilter(2015, 2024)],
    []
  );

  const handleFilterChange: FilterOnChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }) as P2FilterValues);
  };

  return (
    <>
      <ChartFilterBar
        filters={yearRangeFilters}
        values={filters as unknown as FilterValues}
        onChange={handleFilterChange}
        disabled={loading}
      />
      <LineChartCard
        title="Ocupats per sector per cada 1.000 habitants"
        data={chart1Data}
        xKey="any"
        lines={chart1Lines}
        yLabel="Ocupats"
        loading={loading}
        error={error}
        headerExtra={
          <div style={{ display: "flex", gap: 8 }}>
            <Select
              value={filters.sistema}
              options={P2_SISTEMA_OPTIONS}
              onChange={(v) => setFilters((prev) => ({ ...prev, sistema: v }))}
              style={{ width: 160 }}
              disabled={loading}
            />
            <Select
              mode="multiple"
              value={filters.chart1Ambits}
              options={ambitOptions}
              placeholder="Àmbits"
              onChange={(v) => setFilters((prev) => ({ ...prev, chart1Ambits: v }))}
              style={{ minWidth: 200, maxWidth: 480 }}
              maxTagCount="responsive"
              disabled={loading}
            />
          </div>
        }
      />
      <LineChartCard
        title="Comparativa entre sistemes per sector"
        data={chart2Data}
        xKey="any"
        lines={chart2Lines}
        yLabel="Ocupats"
        loading={loading}
        error={error}
        headerExtra={
          <Select
            value={filters.ambit || undefined}
            options={ambitOptions}
            placeholder="Sector"
            onChange={(v) => setFilters((prev) => ({ ...prev, ambit: v }))}
            style={{ width: 280 }}
            disabled={loading}
          />
        }
      />
      <CompareButton />
    </>
  );
};

export default P2ChartSection;
